// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles } from "@fluentui/react";
import type { IRawStyle, ITheme } from "@fluentui/react/lib/Styling";
import {
  concatStyleSets,
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
} from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import type { StandardControlSemanticColors } from "@1js/acui-common";
import {
  commonHighContrastOutlineHoverFocus,
  getHighContrastAutoAdjustStyle,
  getStandardControlSemanticColorsWithTheme,
} from "@1js/acui-common";
import type { AppGalleryStyles } from "@1js/acui-gallery";
import { isNextStyleEnabled } from "@1js/acui-theme";

const galleryButtonClassname = "galleryButton";
const galleryButtonOffset = 4;
const galleryButtonOffsetNext = 2;
const galleryPaddingLeft = 8;
const galleryItemWidth = 108;
const borderWidth = 4;

export const galleryButtonWidth =
  galleryItemWidth + galleryPaddingLeft + borderWidth * 2;

const highContrastLabel = {
  [HighContrastSelector]: {
    borderColor: "transparent",
    color: "ButtonText",
    backgroundColor: "ButtonFace",
    ...getHighContrastNoAdjustStyle(),
  },
};

const getCommonFontStyleGalleryButtonStyles = memoizeFunction(
  (theme: ITheme): IButtonStyles => {
    const semanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return {
      root: [
        galleryButtonClassname,
        {
          boxSizing: "content-box",
          height: 28,
          padding: "0px 0px 0px 8px",
          borderWidth: galleryButtonOffset,
          textAlign: "left",
          color: semanticColors.fontGalleryTextColor,
          selectors: {
            [HighContrastSelector]: {
              borderColor: "transparent",
            },
          },
        },
      ],
      rootDisabled: [
        {
          color: semanticColors.fontGalleryTextDisabledColor,
        },
      ],
      rootHovered: [
        {
          color: semanticColors.fontGalleryTextColor,
        },
      ],
      rootPressed: [
        {
          color: semanticColors.fontGalleryTextColor,
        },
      ],
      rootChecked: [
        {
          color: semanticColors.fontGalleryTextColor,
        },
      ],
      flexContainer: {
        justifyContent: "flex-start",
        backgroundColor: semanticColors.fontGalleryBackgroundColor,
        ...getHighContrastAutoAdjustStyle(),
      },
      label: {
        width: "100%",
        overflow: "hidden",
        whiteSpace: "nowrap",
        display: "block",
        textOverflow: "ellipsis",

        // Without line-height normal, the text disappears in IE 11.
        lineHeight: "normal",
        selectors: {
          ...highContrastLabel,
        },
      },
    };
  }
);

const getPreviewExpandButtonStyles: IButtonStyles = {
  root: {
    height: 34,
    borderTop: "none",
    borderBottom: "none",
  },
};

const getPreviewExpandButtonStylesNext: IButtonStyles = {
  root: {
    height: 30,
  },
};

const getPreviewButtonStyles = (theme: ITheme): IButtonStyles => {
  const semanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  return {
    root: {
      width: galleryItemWidth,
      height: 26,
      padding: isNextStyleEnabled()
        ? galleryButtonOffsetNext
        : galleryButtonOffset,
    },
    rootCheckedHovered: {
      padding: isNextStyleEnabled()
        ? galleryButtonOffsetNext
        : galleryButtonOffset,
    },
    rootHovered: {
      padding: isNextStyleEnabled()
        ? galleryButtonOffsetNext
        : galleryButtonOffset,
    },
    flexContainer: {
      backgroundColor: semanticColors.fontGalleryBackgroundColor,
      overflow: "hidden",
    },
    label: {
      paddingLeft: galleryPaddingLeft,
      textOverflow: "unset",
    },
  };
};

const getExpandedButtonHighContrastStyles = {
  selectors: {
    [HighContrastSelector]: {
      borderColor: "Highlight",
      backgroundColor: "transparent",
      outlineColor: "ButtonText",
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

const getExpandButtonStyles: IButtonStyles = {
  root: {
    height: 24,
    padding: "2px 0px 2px 2px",
    width: 114,
    borderWidth: galleryButtonOffset,
    selectors: {
      ":focus": {
        borderWidth: galleryButtonOffset,
      },
    },
  },
  rootChecked: [
    {
      borderWidth: galleryButtonOffset,
      selectors: {
        ":focus": [
          commonHighContrastOutlineHoverFocus,
          getExpandedButtonHighContrastStyles,
        ],
      },
    },
    getExpandedButtonHighContrastStyles,
  ],
  rootFocused: getExpandedButtonHighContrastStyles,
  rootPressed: getExpandedButtonHighContrastStyles,
  rootHovered: [
    commonHighContrastOutlineHoverFocus,
    getExpandedButtonHighContrastStyles,
  ],
  rootCheckedHovered: [
    commonHighContrastOutlineHoverFocus,
    getExpandedButtonHighContrastStyles,
  ],
  label: {
    paddingLeft: 6,
  },
  flexContainer: {
    selectors: {
      [HighContrastSelector]: {
        // the label has variable height. Flex container has stable (100%) height. This prevents button element background
        // not occluded by the label element from being bigger on top and bottom than the desired 2px padding
        backgroundColor: "ButtonFace",
      },
    },
  },
};

/**
 * Get the styling for the preview gallery
 * @param { Partial<AppGalleryStyles> } customStyles - Optional, custom styles to apply to gallery
 */
export const getFontStyleGalleryStyles = memoizeFunction(
  (
    theme: ITheme,
    customStyles?: Partial<AppGalleryStyles>
  ): Partial<AppGalleryStyles> => {
    return {
      ...customStyles,
      galleryContainer: customStyles && customStyles.galleryContainer,
      galleryButtonInPreview: concatStyleSets(
        getCommonFontStyleGalleryButtonStyles(theme),
        getPreviewButtonStyles(theme),
        customStyles && customStyles.galleryButtonInPreview
      ),
      galleryButtonInExpanded: concatStyleSets(
        getCommonFontStyleGalleryButtonStyles(theme),
        getExpandButtonStyles,
        customStyles && customStyles.galleryButtonInExpanded
      ),
      previewExpandButton: concatStyleSets(
        getPreviewExpandButtonStyles,
        isNextStyleEnabled() && getPreviewExpandButtonStylesNext,
        customStyles && customStyles.previewExpandButton
      ),
    };
  }
);

export const displayNameStylesWithPreviewStyleLabel: IRawStyle = {
  display: "block",
  textAlign: "center",
  fontSize: "10px",
  textOverflow: "ellipsis",
  selectors: {
    ...highContrastLabel,
  },
};
